import { useRouter } from 'next/router'
import { useMemo } from 'react'

import { SchoolContext } from 'app/KinerjaKSPage/hooks/useSchoolContext'
import { parseQuery } from 'utils/router'
import { useApiGetSchoolList } from 'api/kinerja/kepala-sekolah/school'
import { isUserHasPltRole } from 'app/KinerjaKSPage/utils/profile'
import { useAPIKinerjaProfile } from 'api/kinerja/useAPIKinerja'

import type { Schools } from 'types/kinerja-ks'

interface Props {
  preventUnauthorizedAccess?: boolean | string
  children: React.ReactNode
}

export function SchoolProvider({
  preventUnauthorizedAccess = false,
  children,
}: Props) {
  const router = useRouter()

  const schoolId = useMemo(() => {
    // If router is not ready, return null
    if (!router.isReady) return null

    return parseQuery<string>(router?.query?.id || null) as string
  }, [router?.isReady, router?.query?.id])

  const {
    data: { data: profile } = {},
    isFetched: isProfileFetched,
    isLoading: isLoadingProfile,
  } = useAPIKinerjaProfile({
    options: {
      staleTime: 60 * 60 * 1000, // 1 hour
      cacheTime: 60 * 60 * 1000, // 1 hour
    },
  })

  const {
    data: schoolsData,
    isFetched: isSchoolsFetched,
    isLoading: isLoadingSchools,
    isError: isFailedLoadingSchools,
    refetch: refetchSchools,
  } = useApiGetSchoolList({
    // Set to always as the schools data is needed to determine the selected school
    enabled: true,
  })

  /**
   * Retrieves the schools data based on the current state and router status.
   * @returns The schools data if available, otherwise null.
   */
  const schools: Schools | null = useMemo(() => {
    // If router is not ready, return null
    if (!router.isReady) return null

    // If schools are already fetched, return the data
    if (isSchoolsFetched) return schoolsData?.data || null

    return null
  }, [isSchoolsFetched, router.isReady, schoolsData?.data])

  // Check if the user has the KS_PLT role
  const userHasPltRole = isUserHasPltRole(profile)

  // Set to true if there is only one school available
  const isAutoSelectSchool = schools?.length === 1

  /**
   * Returns the selected school based on the provided schoolId and schools array.
   * If the router is not ready, or no school is selected, it returns null.
   * @returns The selected school or null.
   */
  const selectedSchool = useMemo(() => {
    // If router is not ready, return null
    if (!router.isReady) return null

    if (isAutoSelectSchool) return schools[0] || null

    // If no school is selected, return null
    if (!schoolId || !schools) return null

    // Find the school with matching npsn
    const school = schools.find((school) => school.npsn === schoolId)

    return school || null
  }, [isAutoSelectSchool, router.isReady, schoolId, schools])

  const selectedSchoolName = useMemo(() => {
    if (isProfileFetched && isSchoolsFetched)
      return !!selectedSchool
        ? selectedSchool?.schoolName
        : profile?.satuanPendidikan

    return null
  }, [
    isProfileFetched,
    isSchoolsFetched,
    profile?.satuanPendidikan,
    selectedSchool,
  ])

  const leave = (url: string = '/404') => {
    return router.isReady ? router.replace(url) : window.location.replace(url)
  }

  const currentNpsn = selectedSchool?.npsn || null

  const isNpsnValid: boolean = useMemo(() => {
    if (
      isProfileFetched &&
      isSchoolsFetched &&
      schoolId &&
      schools?.filter((school) => school.npsn === schoolId)?.length === 1
    )
      return true

    return false
  }, [isProfileFetched, isSchoolsFetched, schoolId, schools])

  if (
    isProfileFetched &&
    isSchoolsFetched &&
    !isNpsnValid &&
    !!preventUnauthorizedAccess
  ) {
    leave(
      !!preventUnauthorizedAccess &&
        typeof preventUnauthorizedAccess === 'string'
        ? preventUnauthorizedAccess
        : undefined
    )
  }

  return (
    <SchoolContext.Provider
      value={{
        id: schoolId,
        profile,
        isProfileFetched,
        isLoadingProfile,
        userHasPltRole,
        isAutoSelectSchool,
        schools,
        selectedSchool,
        selectedSchoolName,
        currentNpsn,

        isLoadingSchools,
        isFailedLoadingSchools,
        refetchSchools,

        isNpsnValid,
      }}
    >
      {children}
    </SchoolContext.Provider>
  )
}
